import { parsePercent } from '@/utils/number-functions';
import consoleLog from '@/utils/console-log';

export default class PercentMask {
	constructor() {
		this.format = this.format.bind(this);
		this.parse = this.parse.bind(this);
		this.preventDefault = this.preventDefault.bind(this);
		this.setSelectionRange = this.setSelectionRange.bind(this);
		this.setValues = this.setValues.bind(this);
		this.startCaretPos;
		this.endCaretPos;
		this.hasDecimal;
		this.decimalPos;
		this.numDecimals;
		this.initialLength;
		this.input;
		this.event;
		this.keyCode;
	}

	get functionKeyCodes() {
		return Array.from({ length: 12 }, (v, i) => i + 112);
	}

	get actionKeyCodes() {
		return [8, 9, 46, 37, 38, 39, 40, 35, 36];
	}

	get numericKeyCodes() {
		return Array.from({ length: 10 }, (v, i) => i + 48).concat(
			Array.from({ length: 10 }, (v, i) => i + 96)
		);
	}

	get isBackspace() {
		return this.keyCode === 8;
	}
	get isDelete() {
		return this.keyCode === 46;
	}
	get isDecimal() {
		return this.keyCode === 190 || this.keyCode === 110;
	}
	get isActionKey() {
		return this.actionKeyCodes.includes(this.keyCode);
	}

	get events() {
		return {
			keypress: {
				name: 'keypress',
				allowedKeyCodes: this.numericKeyCodes
			},
			keydown: {
				name: 'keydown',
				allowedKeyCodes: this.actionKeyCodes.concat(
					this.numericKeyCodes,
					this.functionKeyCodes
				)
			}
		};
	}

	get keyAllowed() {
		if (this.events[this.eventType]) {
			return this.events[this.eventType].allowedKeyCodes.includes(
				this.keyCode
			);
		}
		return true;
	}

	get currentInputVal() {
		return this.input ? this.input.value : '';
	}

	get currentLength() {
		return this.currentInputVal.length;
	}

	get formattedVal() {
		let valToParse = this.currentInputVal.replace('%', '');
		let valDecPos = valToParse.indexOf('.');
		let valNumDec = 0;

		if (valDecPos > 0) {
			valNumDec = valToParse.length - (valDecPos + 1);
		}
		//if there are more than three decimal places, parse off the extra
		if (valNumDec > 2) {
			valToParse = this.currentInputVal + '';
			valDecPos = valToParse.indexOf('.');
			valToParse = valToParse.substring(0, valDecPos + 3);
		}

		let num = parseFloat(valToParse);
		if (isNaN(num)) return '';
		return `${num.toFixed(2)}%`;
	}

	get eventType() {
		return this.event ? this.event.type : undefined;
	}

	get isUpdateEvent() {
		return ['blur', 'mounted', 'input', 'updated'].includes(this.eventType);
	}

	get cursorAtEnd() {
		return (
			this.hasDecimal &&
			this.numDecimals > 1 &&
			this.startCaretPos === this.initialLength - 1
		);
	}

	get deleteProhibited() {
		if (
			this.isBackspace &&
			this.hasDecimal &&
			this.startCaretPos === this.decimalPos + 1
		) {
			return true;
		}

		if (
			this.isDelete &&
			this.hasDecimal &&
			this.startCaretPos === this.decimalPos
		) {
			return true;
		}

		return false;
	}

	setValues() {
		if (this.eventType === 'keypress' || this.eventType === 'keydown') {
			this.initialValue = this.input.value || '';
			this.startCaretPos = this.input.selectionStart;
			this.endCaretPos = this.input.selectionEnd;
			this.hasDecimal = this.initialValue.includes('.');
			this.decimalPos = this.initialValue.indexOf('.');
			this.initialLength = this.initialValue.length;
			this.keyCode = this.event.which || this.event.keycode;

			if (this.hasDecimal) {
				this.numDecimals = this.initialLength - 1 - (this.decimalPos + 1);
			}
		}
	}

	format(input, e) {
		this.input = input;
		this.event = e;
		this.setValues();

		if (this.isDelete && this.startCaretPos === this.decimalPos + 1) {
			if (this.eventType === 'keydown' && !e.handled) {
				e.preventDefault();

				let newVal =
					this.initialValue.substring(0, this.decimalPos + 1) +
					'0' +
					this.initialValue.substring(this.decimalPos + 2);

				this.input.value = newVal;
				this.input.value = this.formattedVal;

				this.input.setSelectionRange(
					this.decimalPos + 2,
					this.decimalPos + 2
				);

				return;
			}
		}

		this.preventDefault();

		if (this.isUpdateEvent) {
			this.input.value = this.formattedVal;
			this.setSelectionRange();
		} else if (this.deleteProhibited || this.isDecimal) {
			this.setSelectionRange();
		}
	}

	setSelectionRange() {
		let newCaretPos;
		let lengthDiff;
		let newDecimalPos;

		if (this.isDelete) {
			if (this.startCaretPos === this.decimalPos - 1) {
				//if deleting the digit left of the decimal, move past the decimal
				if (this.initialLength !== this.currentLength) {
					//if the delete shortened the val, only move the caret +1
					lengthDiff = 1;
				} else {
					//if the length stayed the same (deleted last digit), move the caret +2
					lengthDiff = 2;
				}
			} else if (this.startCaretPos === this.decimalPos) {
				//if caret to the left of the decimal, move the caret to the other side
				lengthDiff = 1;
			} else if (this.startCaretPos === 0) {

				lengthDiff = 0;
			} else if (this.startCaretPos === this.decimalPos + 1) {
				//if caret is right after the decimal
				lengthDiff = 1;
			} else if (this.startCaretPos === this.endCaretPos) {
				//if doing a normal delete, add one to maintain current position
				lengthDiff =
					this.currentInputVal.length - this.initialLength + 1;
			} else {
				//deleting a selection range
				lengthDiff = this.currentInputVal.length - this.initialLength;
			}
		} else if (
			this.isBackspace &&
			this.currentInputVal.length === this.initialLength
		) {
			//if backspacing and the length doesn't change
			//then we are backspacing a comma or decimal
			//move the caret to the other side
			lengthDiff = -1;
		} else if (this.hasDecimal && this.startCaretPos > this.decimalPos) {
			//typing after the decimal
			lengthDiff = 1;
		} else if (
			!this.hasDecimal &&
			this.startCaretPos === this.initialLength
		) {
			//if no decimal an typing at end of value
			lengthDiff = 1;
		} else {
			//default resetting of caret position
			//adjusts position from startCaretPos by the difference in length after entering
			lengthDiff = this.currentInputVal.length - this.initialLength;
		}

		newCaretPos = this.startCaretPos + lengthDiff;

		newDecimalPos = this.currentInputVal.indexOf('.');

		if (
			this.startCaretPos == 0 &&
			this.endCaretPos <= this.initialLength
		) {
			//if the entire input is selected (or everything except %), move the caret after the first digit typed
			newCaretPos = 1;
		} else if (
			this.startCaretPos > 0 &&
			this.endCaretPos > this.startCaretPos
		) {
			if (this.startCaretPos > this.decimalPos) {
				if (this.isDelete) {
					//if deleting all the decimal places, set caret to the right of the decimal
					newCaretPos = this.decimalPos + 1;
				} else {
					//if multiple digits to the right are selected, move the caret to the end of the selection
					newCaretPos = this.startCaretPos + 1;
				}
			} else {
				if (this.endCaretPos > this.decimalPos) {
					//if the selection spans over the decimal
					newCaretPos = this.startCaretPos;
				} else {
					//if multiple digits to the left are selected, move the caret to the beginning of the selection
					newCaretPos = this.endCaretPos + lengthDiff;
				}
			}
		} else if (this.isDecimal) {
			//if a decimal was typed then move the caret to the right of it
			newCaretPos = this.decimalPos + 1;
		} else if (
			this.hasDecimal &&
			newCaretPos <= this.decimalPos &&
			newCaretPos > newDecimalPos &&
			!this.isDelete
		) {
			//don't let the new caret pos go past the decimal if we were on the left side of it unless we're deleting
			newCaretPos = newDecimalPos;
		}

		this.input.setSelectionRange(newCaretPos, newCaretPos);
	}

	parse() {
		let parsedVal = 0;
		if (this.formattedVal) {
			parsedVal = parseFloat(parsePercent(this.formattedVal));
		}
		return parsedVal;
	}

	preventDefault() {
		if (
			!this.keyAllowed ||
			(this.cursorAtEnd && !this.isActionKey) ||
			this.deleteProhibited
		) {
			consoleLog('Prevented default');
			this.event?.preventDefault();
		}
	}
}
